<template>
  <div class="diagnostic-wrap">
    <div v-if="loading" class="text-center py-5 text-muted">
      <i class="fa-solid fa-spinner fa-spin fa-2x mb-3"></i>
      <p class="mb-0">Verificando diagnóstico...</p>
    </div>

    <div v-else-if="data">
      <!-- Tiles de status -->
      <div class="diagnostic-tiles mb-4">
        <div class="diag-tile" :class="searchPill.class">
          <i :class="searchPill.icon" class="diag-tile-icon"></i>
          <div class="diag-tile-text">
            <div class="diag-tile-caption">Visibilidade na busca</div>
            <div class="diag-tile-status">{{ searchPill.label }}</div>
          </div>
        </div>
        <div class="diag-tile" :class="bookPill.class">
          <i :class="bookPill.icon" class="diag-tile-icon"></i>
          <div class="diag-tile-text">
            <div class="diag-tile-caption">Agendamento</div>
            <div class="diag-tile-status">{{ bookPill.label }}</div>
          </div>
        </div>
      </div>

      <!-- Busca -->
      <div class="diag-section-title">
        <i class="fa-solid fa-magnifying-glass me-2 text-muted"></i>
        Aparecer na busca
      </div>
      <ul class="diag-check-list mb-4">
        <li
          v-for="(c, i) in sortedChecks(data.search_visibility.checks)"
          :key="'sv-' + i"
          class="diag-check"
        >
          <i :class="checkIcon(c).icon" class="diag-check-icon me-2"></i>
          <div class="diag-check-text">
            <div class="diag-check-label">{{ labelFor(c) }}</div>
            <div class="diag-check-detail">{{ c.message }}</div>
          </div>
        </li>
      </ul>

      <!-- Agendamento -->
      <div class="diag-section-title">
        <i class="fa-solid fa-calendar-check me-2 text-muted"></i>
        Aceitar agendamento
      </div>
      <ul class="diag-check-list mb-0">
        <li
          v-for="(c, i) in sortedChecks(data.can_be_booked.checks)"
          :key="'cb-' + i"
          class="diag-check"
        >
          <i :class="checkIcon(c).icon" class="diag-check-icon me-2"></i>
          <div class="diag-check-text">
            <div class="diag-check-label">{{ labelFor(c) }}</div>
            <div class="diag-check-detail">{{ c.message }}</div>
          </div>
        </li>
      </ul>
    </div>

    <div v-else class="text-center py-5 text-muted">
      <i class="fa-solid fa-triangle-exclamation fa-2x mb-3"></i>
      <p class="mb-0">Não foi possível carregar o diagnóstico.</p>
    </div>
  </div>
</template>

<script>
const CHECK_LABELS = {
  // Busca
  not_soft_deleted: "Conta não excluída",
  is_psychologist: "É um psicólogo",
  active_flag: "Conta ativa",
  approved_flag: "Cadastro aprovado",
  charges_enabled: "Stripe Connect apto a receber",
  stripe_id_set: "Stripe Connect conectado",
  not_apple_test_user: "Não é usuário de teste (Apple)",
  has_valid_subscription_not_past_due: "Assinatura válida",
  // Agendamento
  block_agenda_off_for_scheduled: "Agenda liberada",
  online_available: "Atendimento online",
  first_appointment_available: "Desconto de 1ª sessão",
  accepted_gender: "Gênero de atendimento",
};

export default {
  name: "PsiDiagnostic",
  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      data: null,
    };
  },
  watch: {
    userId: {
      immediate: true,
      handler() {
        this.fetchDiagnostic();
      },
    },
  },
  computed: {
    searchPill() {
      const sv = this.data?.search_visibility;
      if (!sv) return {};
      if (sv.will_appear) {
        return {
          class: "diag-ok",
          icon: "fa-solid fa-circle-check",
          label: "Aparece na busca",
        };
      }
      const fails = sv.checks.filter((c) => !c.pass).length;
      return {
        class: "diag-danger",
        icon: "fa-solid fa-circle-xmark",
        label: `Não aparece na busca (${fails} ${fails === 1 ? "problema" : "problemas"})`,
      };
    },
    bookPill() {
      const cb = this.data?.can_be_booked;
      if (!cb) return {};
      if (!cb.no_blocking_issues) {
        return {
          class: "diag-danger",
          icon: "fa-solid fa-circle-xmark",
          label: "Não aceita agendamento",
        };
      }
      const hasInfoFail = cb.checks.some(
        (c) =>
          !c.pass &&
          c.severity === "info" &&
          c.check !== "imediate_available"
      );
      if (hasInfoFail) {
        return {
          class: "diag-warning",
          icon: "fa-solid fa-circle-exclamation",
          label: "Aceita agendamento parcialmente",
        };
      }
      return {
        class: "diag-ok",
        icon: "fa-solid fa-circle-check",
        label: "Aceita agendamento",
      };
    },
  },
  methods: {
    async fetchDiagnostic() {
      if (!this.userId) return;
      this.loading = true;
      this.data = null;
      try {
        const res = await this.$userService.network.get(
          `api/users/${this.userId}/diagnostic`
        );
        this.data = res.data || res;
      } catch (e) {
        console.error("Erro ao carregar diagnóstico:", e);
        this.data = null;
      } finally {
        this.loading = false;
      }
    },
    labelFor(c) {
      return CHECK_LABELS[c.check] || c.message;
    },
    checkIcon(c) {
      if (c.pass) return { icon: "fa-solid fa-circle-check text-success" };
      if (c.severity === "info")
        return { icon: "fa-solid fa-circle-info text-warning" };
      return { icon: "fa-solid fa-circle-xmark text-danger" };
    },
    sortedChecks(checks) {
      // Falhas primeiro; entre falhas, hard antes de info
      return [...checks]
        .filter((c) => c.check !== "imediate_available")
        .sort((a, b) => {
        if (a.pass !== b.pass) return a.pass ? 1 : -1;
        const sev = (c) => (c.severity === "info" ? 1 : 0);
        return sev(a) - sev(b);
      });
    },
  },
};
</script>

<style scoped>
.diagnostic-wrap {
  margin-bottom: 1.5rem;
}

.diagnostic-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6c757d;
  padding: 12px 16px;
}

/* Card com destaque */
.diagnostic-card {
  background: linear-gradient(180deg, #fbfcfe 0%, #f4f6fa 100%);
  border: 1px solid #e3e8ef;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 2px 8px rgba(16, 55, 102, 0.06);
}

.diagnostic-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.diagnostic-title {
  font-weight: 700;
  font-size: 1rem;
  color: #123766;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.diagnostic-tiles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

@media (max-width: 575px) {
  .diagnostic-tiles {
    grid-template-columns: 1fr;
  }
}

.diag-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 10px;
  border: 1px solid transparent;
  border-left-width: 5px;
  background-color: #fff;
}

.diag-tile-icon {
  font-size: 1.6rem;
  flex-shrink: 0;
}

.diag-tile-caption {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8896a6;
  margin-bottom: 2px;
}

.diag-tile-status {
  font-size: 0.95rem;
  font-weight: 700;
}

/* Estados */
.diag-tile.diag-ok {
  border-color: #a3d9bf;
  border-left-color: #198754;
  background-color: #f0fdf4;
}
.diag-tile.diag-ok .diag-tile-icon,
.diag-tile.diag-ok .diag-tile-status {
  color: #198754;
}

.diag-tile.diag-warning {
  border-color: #ffe08a;
  border-left-color: #f59e0b;
  background-color: #fffbeb;
}
.diag-tile.diag-warning .diag-tile-icon,
.diag-tile.diag-warning .diag-tile-status {
  color: #b45309;
}

.diag-tile.diag-danger {
  border-color: #f4b6bc;
  border-left-color: #dc3545;
  background-color: #fef2f2;
}
.diag-tile.diag-danger .diag-tile-icon,
.diag-tile.diag-danger .diag-tile-status {
  color: #dc3545;
}

/* Pills (usadas nos títulos do modal) */
.diag-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.diag-pill.diag-ok {
  color: #0f5132;
  background-color: #d1e7dd;
}

.diag-pill.diag-warning {
  color: #664d03;
  background-color: #fff3cd;
}

.diag-pill.diag-danger {
  color: #842029;
  background-color: #f8d7da;
}

.diag-section-title {
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #123766;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #eef1f5;
}

.diag-check-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.diag-check {
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
  display: flex;
  align-items: flex-start;
}

.diag-check:last-child {
  border-bottom: none;
}

.diag-check-icon {
  font-size: 1rem;
  margin-top: 2px;
}

.diag-check-label {
  font-size: 0.92rem;
  font-weight: 600;
  color: #212529;
  line-height: 1.3;
}

.diag-check-detail {
  font-size: 0.78rem;
  color: #98a2b3;
  line-height: 1.3;
  margin-top: 1px;
}
</style>
